import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import { v4 as uuidv4 } from 'uuid';
import { createFaq, deleteFaqSchema } from "../../../core/actionCreators/faqActionCreator";
import toast from "react-hot-toast";


const FaqForm = (props: any) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const { pageMode, faq } = props

    const { user } = useSelector((s: any) => s.authStore);

    const [faqPayload, setFaqPayload] = useState({
        categoryCode: "",
        categoryName: "",
        modifiedBy: user.id || 33,
        addQuestions: [],
        updateQuestions: []
    } as any)

    useEffect(() => {
        if (pageMode === "EDIT" && Object.keys(faq).length) {
            setFaqPayload({
                ...faqPayload,
                ...faq,
                updateQuestions: faq.faqCategorySchemas
            })
        }
    }, [faq])

    const addFaqSchemaHandler = () => {
        setFaqPayload({
            ...faqPayload,
            addQuestions: [...faqPayload.addQuestions, {
                questionText: "",
                answerText: "",
                id: uuidv4()
            }]
        })
    }

    const deleteUnsavedFaqSchema = (id: string) => {
        setFaqPayload({
            ...faqPayload,
            addQuestions: faqPayload.addQuestions.filter((d: any) => d.id !== id)
        })
    }

    const deleteSavedFaqSchema = (id: string) => {
        let res = window.confirm("Are you sure want to delete?")
        if (res) {
            setFaqPayload({
                ...faqPayload,
                updateQuestions: faqPayload.updateQuestions.filter((d: any) => d.id !== id)
            })
            dispatch(deleteFaqSchema({ id: id }, null))
        }
        return
    }

    const onFaqSchemaChange = (id: string, type: string, value: string, action: string) => {
        let curentFaqSchemaIndex = faqPayload[action].findIndex((d: any) => d.id == id)
        if (curentFaqSchemaIndex === -1) {
            return
        }
        let curentFaqSchema = faqPayload[action][curentFaqSchemaIndex]
        curentFaqSchema[type] = value
        faqPayload[action][curentFaqSchemaIndex] = curentFaqSchema
        setFaqPayload({
            ...faqPayload,
            addQuestions: [...faqPayload.addQuestions],
            updateQuestions: [...faqPayload.updateQuestions]
        })
    }

    const saveFaqSchemaHandler = () => {
        faqPayload.addQuestions.forEach((d: any) => delete d.id)
        dispatch(createFaq(faqPayload, () => {
            toast.success("Saved successfully")
            navigate("/faq")
        }))
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header Section */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">
                        {pageMode === "EDIT" ? "Edit FAQ Schema" : "Create FAQ Schema"}
                    </h1>
                    <p className="mt-2 text-gray-600">Manage frequently asked questions</p>
                </div>
                
                {/* Category Information Card */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                        Category Information
                    </h2>
                    
                    <form>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Category Code *
                                </label>
                                <input
                                    type="text"
                                    disabled={pageMode === "EDIT"}
                                    required
                                    value={faqPayload.categoryCode}
                                    onChange={(e) => setFaqPayload({ ...faqPayload, categoryCode: e.target.value })}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50 disabled:text-gray-500"
                                    placeholder="e.g., electronics, tools, safety"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Category Name *
                                </label>
                                <input
                                    type="text"
                                    disabled={pageMode === "EDIT"}
                                    required
                                    value={faqPayload.categoryName}
                                    onChange={(e) => setFaqPayload({ ...faqPayload, categoryName: e.target.value })}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors disabled:bg-gray-50 disabled:text-gray-500"
                                    placeholder="e.g., Electronics & Appliances"
                                />
                            </div>
                        </div>
                    </form>
                </div>

                {/* FAQ Questions Section */}
                <div className="space-y-6">
                    {/* Existing Questions (from API) */}
                    {faqPayload.updateQuestions && faqPayload.updateQuestions.map((question: any, index: number) => (
                        <div key={question.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <div className="flex justify-between items-center mb-4">
                                <div className="flex items-center space-x-3">
                                    <h3 className="text-lg font-semibold text-gray-900">
                                        FAQ #{index + 1}
                                    </h3>
                                    {/* AI Generated Flag */}
                                    {question.aiGenerated && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                            🤖 AI Generated
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center space-x-3">
                                    {/* Delete Button */}
                                    <button
                                        onClick={() => deleteSavedFaqSchema(question.id)}
                                        className="inline-flex items-center px-3 py-1.5 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors text-sm"
                                    >
                                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                        Delete
                                    </button>
                                </div>
                            </div>
                            
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Question</label>
                                    <textarea
                                        value={question.questionText || ''}
                                        onChange={(e) => onFaqSchemaChange(question.id, "questionText", e.target.value, "updateQuestions")}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                                        rows={2}
                                        placeholder="Enter your question here..."
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Answer</label>
                                    <textarea
                                        value={question.answerText || ''}
                                        onChange={(e) => onFaqSchemaChange(question.id, "answerText", e.target.value, "updateQuestions")}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                                        rows={4}
                                        placeholder="Enter your answer here..."
                                    />
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* New Questions */}
                    {faqPayload.addQuestions && faqPayload.addQuestions.map((question: any, index: number) => (
                        <div key={question.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 border-l-4 border-l-green-500">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">
                                    FAQ #{faqPayload.updateQuestions.length + index + 1} (New)
                                </h3>
                                <div className="flex items-center space-x-3">
                                    {/* Delete Button */}
                                    <button
                                        onClick={() => deleteUnsavedFaqSchema(question.id)}
                                        className="inline-flex items-center px-3 py-1.5 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors text-sm"
                                    >
                                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                        Delete
                                    </button>
                                </div>
                            </div>
                            
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Question</label>
                                    <textarea
                                        value={question.questionText || ''}
                                        onChange={(e) => onFaqSchemaChange(question.id, "questionText", e.target.value, "addQuestions")}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                                        rows={2}
                                        placeholder="Enter your question here..."
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Answer</label>
                                    <textarea
                                        value={question.answerText || ''}
                                        onChange={(e) => onFaqSchemaChange(question.id, "answerText", e.target.value, "addQuestions")}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                                        rows={4}
                                        placeholder="Enter your answer here..."
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Action Buttons */}
                <div className="mt-8 flex justify-between items-center">
                    <button
                        onClick={addFaqSchemaHandler}
                        className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                        </svg>
                        Add Question
                    </button>

                    <div className="flex space-x-4">
                        <button
                            onClick={() => navigate("/faq")}
                            className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                        >
                            Cancel
                        </button>
                        
                        <button
                            onClick={saveFaqSchemaHandler}
                            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                            {pageMode === "EDIT" ? "Update FAQ Schema" : "Save FAQ Schema"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}
export default FaqForm;